import {useMemo} from 'react';
import {useSelector} from 'react-redux';

import BadgeCardCompact from '@/components/BadgeCard/BadgeCardCompact';
import BadgesGrid from '@/components/BadgesGrid/BadgesGrid';
import ButtonRaw from '@/components/Buttons/ButtonRaw';
import {RightArrowIcon} from '@/components/Icons';
import {BADGE_TRACKING} from '@/consts/badgeTracking';
import useBadgeDetailRoute from '@/hooks/useBadgeDetailRoute';
import {getBadgeDetailsContent} from '@/services/content/appContent';
import {trackBadgeClicked} from '@/services/events/loyaltyEventTracking';
import appRouterService from '@/services/route/appRouterService';
import {selectAvailableBadges} from '@/state/selectors/loyalty';

const MAX_BADGES = 2;

type BadgeOtherBadgesProps = {
    currentBadgeId: string;
};

const BadgeOtherBadges = ({currentBadgeId}: BadgeOtherBadgesProps) => {
    const {singleLevelBadgeDetailsContent} = getBadgeDetailsContent();
    const {otherBadgesTitle, otherBadgesViewAllButton, otherBadgesBadgeBackgroundImage} =
        singleLevelBadgeDetailsContent ?? {};
    const allAvailable = useSelector(selectAvailableBadges);
    const {openBadge} = useBadgeDetailRoute();

    const badges = useMemo(
        () => allAvailable.filter((b) => b.id !== currentBadgeId).slice(0, MAX_BADGES),
        [allAvailable, currentBadgeId]
    );

    if (!badges.length) return null;

    return (
        <div className='flex flex-col gap-4 px-4'>
            <div className='flex w-full flex-row items-center justify-between'>
                {otherBadgesTitle && (
                    <span className='font-bold text-20 leading-7 tracking-02'>{otherBadgesTitle}</span>
                )}
                {otherBadgesViewAllButton && (
                    <ButtonRaw
                        className='flex gap-2 border-none bg-transparent p-0 text-16 text-romance leading-6 tracking-05'
                        onClick={appRouterService.replaceToBadgesPage}
                        data-testid='other-badges-view-all-button'
                    >
                        {otherBadgesViewAllButton.text && <span>{otherBadgesViewAllButton.text}</span>}
                        <RightArrowIcon />
                    </ButtonRaw>
                )}
            </div>
            <BadgesGrid
                badges={badges}
                className='[&>*:only-child]:col-span-2'
                renderCard={(badge) => (
                    <BadgeCardCompact
                        badge={badge}
                        testId='other-badges-badge-card-button'
                        backgroundImage={otherBadgesBadgeBackgroundImage}
                        onClick={() => {
                            trackBadgeClicked(badge.title, BADGE_TRACKING.PAGE_IDS.BADGE_OVERVIEW);
                            openBadge(badge, {replace: true});
                        }}
                    />
                )}
            />
        </div>
    );
};

export default BadgeOtherBadges;
